import { boxShadowLevel2 } from '@issuu/ixu';
import {
    colorBaseWhite,
    colorTextLinkVioletLightDefault,
    layoutMarginSmall,
    Link,
    LinkColor,
    ProductButton,
    ProductHeading,
    ProductParagraph,
    Spacing,
} from '@issuu/silkscreen';
import React, { useCallback, useContext } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { HEADER_HEIGHT } from '../../constants';
import { shouldShowAds } from '../../util/ads-utils';
import { AdsCoverBrief } from '../Ads';
import DocumentContext from '../DocumentPage/DocumentContext';
import { TextOverflow } from '../shared/styles';

const Root = styled.section`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    position: relative;
    margin: -${layoutMarginSmall};
    margin-bottom: 0;
    grid-area: cover-brief;
    height: calc(100vh - ${HEADER_HEIGHT});
    color: ${colorBaseWhite};

    a {
        font-weight: 600;
    }

    a:visited {
        color: ${colorTextLinkVioletLightDefault};
    }
`;

const Backdrop = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgb(61, 66, 60) linear-gradient(180deg, transparent, #000);
    z-index: -1;
`;

const CoverImage = styled.img<{ fullWidth: number; fullHeight: number }>`
    position: relative;
    display: block;
    ${props =>
        css`
            aspect-ratio: calc(${props.fullWidth} / ${props.fullHeight});
            height: calc(20rem * ${props.fullHeight} / ${props.fullWidth});
        `}
    max-width: 20rem;
    min-height: 10rem;
    flex-shrink: 1;
    margin: calc(${layoutMarginSmall} * 2) auto;
    box-shadow: ${boxShadowLevel2};
    transition: box-shadow 0.15s;
`;

const scrollAnimation = keyframes`
    0% {
        opacity: 0.1;
    }

    10% {
        transform: translateY(0);
        opacity: 1;
    }

    100% {
        transform: translateY(calc(1rem * -2.5));
        opacity: 0;
    }
}
`;

const WrapText = styled(TextOverflow)`
    text-align: center;
    padding: 0 ${layoutMarginSmall};
`;

const Scroll = styled.div`
    padding-top: calc(${layoutMarginSmall} * 2.5);
    margin: ${layoutMarginSmall};
`;

const Scroller = styled.div`
    width: 1rem;
    height: 1rem;
    border-radius: 100%;
    background-color: #fff;
    animation-name: ${scrollAnimation};
    animation-duration: 2.8s;
    animation-timing-function: cubic-bezier(0.05, 0.61, 0.69, 0.94);
    animation-iteration-count: infinite;
    margin: auto;
    margin-bottom: ${layoutMarginSmall};
`;

export const MobileCoverBrief: React.FC = () => {
    const context = useContext(DocumentContext);
    const {
        hosts: { image: imageHost },
        document: {
            fullPageImageDimensions: [{ width: fullWidth, height: fullHeight }],
            publicationId,
            revisionId,
            title,
        },
        publisher: { displayName: publisherDisplayName, username: publisherUsername },
        appState: { readerRef },
    } = context;

    const scrollToReader = useCallback(() => {
        readerRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }, [readerRef]);

    return (
        <Root aria-label="Cover">
            <Backdrop />
            {shouldShowAds({ context }) ? <AdsCoverBrief /> : null}
            <CoverImage
                alt=""
                fullHeight={fullHeight}
                fullWidth={fullWidth}
                // FIXME: get more suitable images and their dimensions
                src={`//${imageHost}/${revisionId}-${publicationId}/jpg/page_1_thumb_large.jpg`}
            />
            <WrapText>
                <ProductHeading step="negative-2">{title}</ProductHeading>
            </WrapText>
            <WrapText>
                <ProductParagraph step="negative-2">
                    from{' '}
                    <Link color={LinkColor.White} href={`/${publisherUsername}`}>
                        {publisherDisplayName}
                    </Link>
                </ProductParagraph>
            </WrapText>
            <Spacing bottom={1} top={4}>
                <ProductButton onClick={scrollToReader} role="link" size="large" variant="primary">
                    Read Now
                </ProductButton>
            </Spacing>
            <Scroll>
                <Scroller />
                <ProductParagraph step="negative-2">Scroll for more</ProductParagraph>
            </Scroll>
        </Root>
    );
};
